import React, { useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { AlertContent, ModalFactory, ModalText, Text } from 'components';
import { selectAPIRequestStatus, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { CalculatorRules, updateCalculatorRulesRequest } from 'store/CalculatorRules';
import { getPosCategoriesRequest, POSCategories } from 'store/Collections';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { IStore } from 'store/types';
import { ICalculatorRules, ICategoryGroup, ICreateUpdateCalculatorRules, PermissionType, RequestStatus, ShareRuleType, TipCalculatorMethod } from 'types';
import { hasSitePermission } from 'utils/employers';
import { toTitleCase } from 'utils/strings';
import { AddButton, TipCalculationsContainer, Title } from '../styles';
import { CategoriesLoader } from './components/CategoriesLoader';
import { CreateSalesGroup } from './components/CreateSalesGroup';
import { SalesGroupItem } from './components/SalesGroupItem';
import { ModalWarning, TipCalculationsDescription } from './components/styles';

interface ISalesGroupsFormProps {
  calculatorRules?: ICalculatorRules;
}

export const SalesGroupsForm: React.FC<ISalesGroupsFormProps> = ({ calculatorRules }) => {
  const dispatch = useDispatch();
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, CalculatorRules.UPDATE_CALCULATOR_RULES_REQUEST));
  const isLoadingPosCategories = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, POSCategories.GET_POS_CATEGORIES_REQUEST));
  const getPosCategoriesStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, POSCategories.GET_POS_CATEGORIES_REQUEST));
  const [showLoader, setShowLoader] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<ICategoryGroup>();
  const editPermission = hasSitePermission(currentSite?.permissions, PermissionType.CALCULATOR_RULES_UPDATE);

  const onConfirm = (action: string, vals: ICategoryGroup, onSuccess?: () => void) => {
    let successMessage = '';
    const newCalculatorRules: ICreateUpdateCalculatorRules = structuredClone(calculatorRules) || { method: TipCalculatorMethod.NONE, rules: [], categoryGroups: [] };

    switch (action) {
      case 'create':
        successMessage = `${vals.name} created successfully`;
        newCalculatorRules.categoryGroups.push(vals);
        break;
      case 'edit':
        successMessage = 'Sales Group updated successfully';
        newCalculatorRules.categoryGroups = calculatorRules.categoryGroups.map((group) => (group.name === selectedGroup.name ? vals : group));
        if (vals.name !== selectedGroup?.name) {
          // update old group name
          calculatorRules?.rules.forEach((rule, i) => {
            rule.sharePolicy?.shares.forEach((share, j) => {
              if (share.type === ShareRuleType.CATEGORY_GROUP && share.category === selectedGroup?.name) {
                newCalculatorRules.rules[i].sharePolicy.shares[j].category = vals.name;
              }
            });
          });
        }
        break;
      case 'remove':
        successMessage = `${vals.name} deleted successfully`;
        newCalculatorRules.categoryGroups = calculatorRules.categoryGroups.filter((group) => group.name !== selectedGroup.name);
        break;
      default:
        break;
    }
    dispatch(updateCalculatorRulesRequest(currentSite.id, newCalculatorRules, onSuccess, successMessage));
  };

  const [showCreateSalesGroup, hideCreateSalesGroup] = useModal(
    () => <CreateSalesGroup hideModal={hideCreateSalesGroup} calculatorRules={calculatorRules} onConfirm={onConfirm} isLoading={isLoading} selectedGroup={selectedGroup} />,
    [calculatorRules, isLoading, selectedGroup]
  );

  const [showPosCategoriesLoader, hidePosCategoriesLoader] = useModal(() => <CategoriesLoader />, []);

  const loadPosCategories = () => {
    showPosCategoriesLoader();
    dispatch(getPosCategoriesRequest(currentSite.id));
    setShowLoader(true);
  };

  const onEditSalesGroup = (group: ICategoryGroup) => {
    setSelectedGroup(group);
    loadPosCategories();
  };

  const onCreateSalesGroup = () => {
    setSelectedGroup(undefined);
    loadPosCategories();
  };

  useEffect(() => {
    if (!isLoadingPosCategories && showLoader) {
      hidePosCategoriesLoader();
      setShowLoader(false);

      if (getPosCategoriesStatus === RequestStatus.SUCCESS) {
        showCreateSalesGroup();
      }
    }
  }, [isLoadingPosCategories]);

  const [showDeleteModal, hideDeleteModal] = useModal(() => {
    // check if selected sales group is being used in share rules
    const uses = calculatorRules?.rules.reduce((sharesAcc, rule) => {
      if (rule.sharePolicy?.shares.some((share) => share.type === ShareRuleType.CATEGORY_GROUP && share.category === selectedGroup?.name)) {
        sharesAcc.push(`${toTitleCase(rule.contributor.name)} Share`);
      }
      return sharesAcc;
    }, []);
    return (
      <ModalFactory customModal onClose={hideDeleteModal} isActive>
        {uses.length === 0 ? (
          <AlertContent
            title={`Delete ${toTitleCase(selectedGroup.name)}?`}
            message="This will remove all of the sales categories associated with this group and cannot be undone."
            leftActionLabel="Cancel"
            rightActionLabel="Remove"
            onLeftAction={hideDeleteModal}
            onRightAction={() => onConfirm('remove', selectedGroup, hideDeleteModal)}
            isLoading={isLoading}
            rightActionAppearance="destructive"
            maxWidth={420}
          />
        ) : (
          <AlertContent
            title={`${toTitleCase(selectedGroup.name)} Cannot Be Deleted`}
            message="This sales group is currently in use by multiple Tip Shares. Please update those Tip Share rules before removing the sales group."
            hideActions
            onClose={hideDeleteModal}
            maxWidth={420}
          >
            <ModalWarning>
              <Text size="small" fontWeight="semibold" className="title">
                Sales Group In Use By
              </Text>
              <ul>
                {uses.map((share) => (
                  <li key={share}>
                    <ModalText>{share}</ModalText>
                  </li>
                ))}
              </ul>
            </ModalWarning>
          </AlertContent>
        )}
      </ModalFactory>
    );
  }, [calculatorRules, selectedGroup, isLoading]);

  const handleDelete = (group: ICategoryGroup) => {
    setSelectedGroup(group);
    showDeleteModal();
  };

  return (
    <TipCalculationsContainer data-cy="sales-groups-form">
      <Title fontWeight="medium">Sales Groups</Title>
      <TipCalculationsDescription size="xsmall">
        Simplify Tip Share Rules by creating Sales Groups, which are collections of sales categories. For instance, you can create an Alcohol Sales group that includes Liquor, Beer and Wine and simply use Alcohol Sales in Tip Sharing Rules.
      </TipCalculationsDescription>
      {calculatorRules?.categoryGroups.map((group) => (
        <SalesGroupItem key={group.name} categoryGroup={group} onEdit={() => onEditSalesGroup(group)} onDelete={() => handleDelete(group)} disabled={!editPermission} />
      ))}
      {editPermission && <AddButton appearance="white" icon="Plus" label="Create Sales Group" data-cy="create-sales-group-button" onClick={onCreateSalesGroup} />}
    </TipCalculationsContainer>
  );
};

export default SalesGroupsForm;
